/**
 * Global API request wrapper with automatic token refresh on 401
 * 
 * This wrapper intercepts 401 Unauthorized responses and attempts to:
 * 1. Refresh the access token using the refresh token (HTTP-only cookie)
 * 2. Retry the original request with the new token
 * 3. If refresh fails, logout the user and redirect to login
 * 
 * Usage:
 *   import { apiRequest } from '../utils/apiRequest';
 *   const data = await apiRequest('/api/resource', { method: 'GET' });
 */

import { BACKEND_URL, API } from "./apiBase";

// Re-export for callers that import these from this module
export { BACKEND_URL, API };

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

/**
 * Attempt to refresh the access token
 * @returns {Promise<string>} New access token
 */
const refreshAccessToken = async () => {
  const response = await fetch(`${API}/auth/refresh-token`, {
    method: 'POST',
    credentials: 'include', // Include HTTP-only cookies
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Token refresh failed');
  }

  const data = await response.json();
  const newToken = data.access_token;
  
  // Update token in localStorage
  localStorage.setItem('token', newToken);
  
  return newToken;
};

/**
 * Logout user and redirect to login page
 */
const logoutUser = () => {
  localStorage.removeItem('token');
  window.location.href = '/login';
};

/**
 * Make an API request with automatic token refresh on 401
 * 
 * @param {string} url - The API endpoint (full URL or path starting with /api/)
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<any>} Response data
 */
export const apiRequest = async (url, options = {}) => {
  // Ensure full URL
  const fullUrl = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  
  // Get token from localStorage
  const token = localStorage.getItem('token');
  
  // Prepare headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  // Add authorization header if token exists
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Make the request
  const requestOptions = {
    ...options,
    headers,
    credentials: 'include' // Include cookies for refresh token
  };
  
  try {
    const response = await fetch(fullUrl, requestOptions);
    
    // If not 401, handle normally
    if (response.status !== 401) {
      if (!response.ok) {
        // Handle other HTTP errors
        const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      return await response.json();
    }
    
    // Handle 401 Unauthorized - attempt token refresh
    if (!isRefreshing) {
      isRefreshing = true;
      
      try {
        const newToken = await refreshAccessToken();
        isRefreshing = false;
        processQueue(null, newToken);
        
        // Retry the original request with new token
        const retryHeaders = {
          ...headers,
          'Authorization': `Bearer ${newToken}`
        };
        
        const retryResponse = await fetch(fullUrl, {
          ...requestOptions,
          headers: retryHeaders
        });
        
        if (!retryResponse.ok) {
          const errorData = await retryResponse.json().catch(() => ({ detail: 'Request failed' }));
          throw new Error(errorData.detail || `HTTP ${retryResponse.status}`);
        }
        
        return await retryResponse.json();
        
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);
        
        // Refresh failed - logout user
        console.error('Token refresh failed:', refreshError);
        logoutUser();
        throw refreshError;
      }
    }
    
    // If already refreshing, queue this request
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).then(newToken => {
      // Retry with new token
      const retryHeaders = {
        ...headers,
        'Authorization': `Bearer ${newToken}`
      };
      
      return fetch(fullUrl, {
        ...requestOptions,
        headers: retryHeaders
      }).then(async (retryResponse) => {
        if (!retryResponse.ok) {
          const errorData = await retryResponse.json().catch(() => ({ detail: 'Request failed' }));
          throw new Error(errorData.detail || `HTTP ${retryResponse.status}`);
        }
        return await retryResponse.json();
      });
    });
    
  } catch (error) {
    // Network errors or other exceptions
    if (error.message.includes('Token refresh failed') || error.message.includes('Failed to fetch')) {
      // Don't throw, already handled by logoutUser
      return;
    }
    throw error;
  }
};

/**
 * Helper function for GET requests
 */
export const apiGet = (url) => {
  return apiRequest(url, { method: 'GET' });
};

/**
 * Helper function for POST requests
 */
export const apiPost = (url, data) => {
  return apiRequest(url, {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

/**
 * Helper function for PATCH requests
 */
export const apiPatch = (url, data) => {
  return apiRequest(url, {
    method: 'PATCH',
    body: JSON.stringify(data)
  });
};

/**
 * Helper function for DELETE requests
 */
export const apiDelete = (url) => {
  return apiRequest(url, { method: 'DELETE' });
};
